import axiosInstance, { setAxiosToken } from "../../../common/infrastructure/helpers/axiosHelper";
import { endpointsMetadata } from "../../../common/infrastructure/routes/endpointsMetadata";
import {
  isHeicImage,
  prepareImageForUpload,
} from "../../../common/infrastructure/utilities/imageUploadUtility";
import { AI_MAX_IMAGE_SIDE_PX, IAiUploadedImage } from "../models/aiAssistantModels";
import { normalizeUploadedImage } from "./aiAssistantDataUtility";

/**
 * Images for the assistant are made small in the browser before they are uploaded: HEIC becomes
 * JPEG (the shared prepareImageForUpload), then anything longer than 1568 px on its long side is
 * scaled down, the size Claude reads images at. A phone photo goes from several MB to a few
 * hundred KB, which is faster to upload and costs no extra tokens.
 */

export type AiImageErrorReason = "tooBig" | "unreadable";

export class AiImageError extends Error {
  readonly reason: AiImageErrorReason;

  constructor(reason: AiImageErrorReason) {
    super(reason);
    this.reason = reason;
  }
}

export interface IAiPreparedImage {
  blob: Blob;
  fileName: string;
}

/** Types Claude reads as they are; anything else is re-encoded. */
const DIRECT_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

const JPEG_QUALITY = 0.85;

/** JPEG has no transparency: a transparent screenshot is flattened onto white, not black. */
const JPEG_BACKGROUND = "#ffffff";

const IMAGE_EXTENSION = /\.(jpe?g|png|gif|webp|bmp|heic|heif)$/i;

/** Some phones hand files over without a MIME type, so the extension counts as well. */
export const isAcceptedImageFile = (file: File): boolean =>
  file.type.startsWith("image/") || isHeicImage(file) || (!file.type && IMAGE_EXTENSION.test(file.name));

const replaceExtension = (fileName: string, extension: string): string => {
  const base = fileName.replace(/\.[^./\\]+$/, "") || "image";
  return `${base}.${extension}`;
};

interface ILoadedImage {
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
}

const loadImage = async (blob: Blob): Promise<ILoadedImage> => {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(blob);
      return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close() };
    } catch {
      // Older Safari cannot decode some types this way; the <img> route below still can.
    }
  }

  const url = URL.createObjectURL(blob);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error("decode"));
      element.src = url;
    });
    return {
      source: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      release: () => URL.revokeObjectURL(url),
    };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
};

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

/**
 * Scales the image down to at most {@link AI_MAX_IMAGE_SIDE_PX} on its long side. A PNG (usually
 * a screenshot, where JPEG blurs the text) stays PNG when it fits `maxBytes`; everything else
 * becomes a JPEG at quality 0.85. An image that is already small enough is sent untouched.
 */
export const downscaleImage = async (
  blob: Blob,
  fileName: string,
  maxBytes: number,
): Promise<IAiPreparedImage> => {
  const isDirectType = DIRECT_TYPES.includes(blob.type);

  let image: ILoadedImage;
  try {
    image = await loadImage(blob);
  } catch {
    if (isDirectType && blob.size <= maxBytes) return { blob, fileName };
    throw new AiImageError("unreadable");
  }

  try {
    const longSide = Math.max(image.width, image.height);
    if (longSide <= AI_MAX_IMAGE_SIDE_PX && isDirectType && blob.size <= maxBytes) {
      return { blob, fileName };
    }

    const scale = longSide > AI_MAX_IMAGE_SIDE_PX ? AI_MAX_IMAGE_SIDE_PX / longSide : 1;
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(image.width * scale));
    canvas.height = Math.max(1, Math.round(image.height * scale));
    const context = canvas.getContext("2d");
    if (!context) throw new AiImageError("unreadable");

    if (blob.type === "image/png") {
      context.drawImage(image.source, 0, 0, canvas.width, canvas.height);
      const png = await canvasToBlob(canvas, "image/png");
      if (png && png.size <= maxBytes) {
        return { blob: png, fileName: replaceExtension(fileName, "png") };
      }
      context.clearRect(0, 0, canvas.width, canvas.height);
    }

    context.fillStyle = JPEG_BACKGROUND;
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image.source, 0, 0, canvas.width, canvas.height);
    const jpeg = await canvasToBlob(canvas, "image/jpeg", JPEG_QUALITY);
    if (!jpeg) throw new AiImageError("unreadable");
    return { blob: jpeg, fileName: replaceExtension(fileName, "jpg") };
  } finally {
    image.release();
  }
};

/** HEIC → JPEG, then {@link downscaleImage}; throws {@link AiImageError} for an unusable file. */
export const prepareAiImage = async (file: File, maxBytes: number): Promise<IAiPreparedImage> => {
  let prepared: { blob: Blob; fileName: string };
  try {
    prepared = await prepareImageForUpload(file);
  } catch {
    throw new AiImageError("unreadable");
  }

  const resized = await downscaleImage(prepared.blob, prepared.fileName, maxBytes);
  if (resized.blob.size > maxBytes) throw new AiImageError("tooBig");
  return resized;
};

/** POST AiAssistant/UploadImage as multipart, like the support chat's attachments. */
export const uploadAiImage = async (
  image: IAiPreparedImage,
  authToken: string | undefined,
  onProgress: (progress: number) => void,
): Promise<IAiUploadedImage> => {
  setAxiosToken(authToken);

  const formData = new FormData();
  formData.append("file", image.blob, image.fileName);

  const response = await axiosInstance.post(endpointsMetadata.aiAssistant.uploadImage, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (event: ProgressEvent) => {
      if (event.total) onProgress(Math.min(1, event.loaded / event.total));
    },
  });

  const uploaded = normalizeUploadedImage(response.data, image.fileName);
  if (!uploaded) throw new Error("The upload answer has no id.");
  return uploaded;
};
